import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, Star, Trash2 } from 'lucide-react';
import type { ItemStatus, UpdateItemRequest } from '@central/types';
import { useItem, useUpdateItem, useDeleteItem, useItemVersions } from '@/hooks/useItems';
import { useFavoriteIds, useToggleFavorite } from '@/hooks/useFavorites';
import { useRelations } from '@/hooks/useRelations';
import { ItemStatusBadge, TIPO_LABELS } from '@/components/ItemStatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';

const STATUS_OPTIONS: ItemStatus[] = [
  'novo-revisar',
  'rascunho',
  'em-producao',
  'publicado',
  'pausado',
  'arquivado',
  'desaparecido',
];

export function ItemDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const navigate = useNavigate();
  const { data: item, isLoading } = useItem(id);
  const { data: versionsData } = useItemVersions(id);
  const { data: relationsData } = useRelations(id);
  const { data: favData } = useFavoriteIds();
  const toggleFav = useToggleFavorite();
  const updateItem = useUpdateItem(id);
  const deleteItem = useDeleteItem();
  const user = useAuthStore((s) => s.user);
  const isFavorited = favData?.ids.includes(id) ?? false;
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const [editing, setEditing] = useState(false);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<ItemStatus>('novo-revisar');
  const [tagsStr, setTagsStr] = useState('');
  const [techStackStr, setTechStackStr] = useState('');

  if (isLoading || !item) {
    return <div className="text-sm text-muted-foreground">Carregando…</div>;
  }

  function startEdit(): void {
    if (!item) return;
    setNome(item.nome);
    setDescricao(item.descricao ?? '');
    setUrl(item.url ?? '');
    setStatus(item.status as ItemStatus);
    setTagsStr(item.tags.join(', '));
    setTechStackStr(item.tech_stack.join(', '));
    setEditing(true);
  }

  async function handleSave(e: FormEvent): Promise<void> {
    e.preventDefault();
    const payload: UpdateItemRequest = {
      nome,
      descricao: descricao || undefined,
      url: url || undefined,
      status,
      tags: tagsStr
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      tech_stack: techStackStr
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    };
    await updateItem.mutateAsync(payload);
    setEditing(false);
  }

  async function handleDelete(): Promise<void> {
    if (!confirm('Excluir este item? Essa ação não volta.')) return;
    await deleteItem.mutateAsync(id);
    navigate(`/categoria/${item?.tipo ?? ''}`);
  }

  return (
    <div className="space-y-4 max-w-4xl">
      {/* Breadcrumb */}
      <nav className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">Dashboard</Link> ›{' '}
        <Link to={`/categoria/${item.tipo}`} className="hover:text-foreground">
          {TIPO_LABELS[item.tipo]}
        </Link>{' '}
        › {item.nome}
      </nav>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <ItemStatusBadge status={item.status as ItemStatus} />
            <Badge variant="outline" className="text-[10px]">{TIPO_LABELS[item.tipo]}</Badge>
            {item.origem !== 'manual' && (
              <Badge variant="secondary" className="text-[10px]">{item.origem}</Badge>
            )}
          </div>
          <h1 className="text-2xl font-semibold">{item.nome}</h1>
          {item.url && (
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-1"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {item.url}
            </a>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            size="icon"
            variant={isFavorited ? 'default' : 'outline'}
            onClick={() => toggleFav.mutate({ itemId: id, isFavorited })}
            title={isFavorited ? 'Remover dos favoritos' : 'Favoritar'}
          >
            <Star className={cn('h-4 w-4', isFavorited && 'fill-current')} />
          </Button>
          {canEdit && !editing && (
            <Button variant="outline" onClick={startEdit}>Editar</Button>
          )}
          {canEdit && user?.role === 'admin' && !editing && (
            <Button variant="ghost" onClick={handleDelete} title="Excluir">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Visão geral</TabsTrigger>
          <TabsTrigger value="connections">
            Conexões (
            {(relationsData?.counts.outgoing ?? 0) + (relationsData?.counts.backlinks ?? 0)})
          </TabsTrigger>
          <TabsTrigger value="history">Histórico ({versionsData?.total ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {editing ? (
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label htmlFor="nome" className="block text-sm font-medium mb-1">Nome *</label>
                <Input id="nome" required value={nome} onChange={(e) => setNome(e.target.value)} />
              </div>
              <div>
                <label htmlFor="url" className="block text-sm font-medium mb-1">URL</label>
                <Input id="url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
              </div>
              <div>
                <label htmlFor="descricao" className="block text-sm font-medium mb-1">Descrição</label>
                <Textarea
                  id="descricao"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="status" className="block text-sm font-medium mb-1">Status</label>
                  <select
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as ItemStatus)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium mb-1">Tags (vírgula)</label>
                  <Input id="tags" value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} />
                </div>
              </div>
              <div>
                <label htmlFor="techstack" className="block text-sm font-medium mb-1">
                  Tech stack (vírgula)
                </label>
                <Input
                  id="techstack"
                  value={techStackStr}
                  onChange={(e) => setTechStackStr(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={updateItem.isPending}>
                  {updateItem.isPending ? 'Salvando…' : 'Salvar'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {item.descricao && (
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm whitespace-pre-wrap">{item.descricao}</p>
                  </CardContent>
                </Card>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Slug" value={item.slug} mono />
                <Field label="Path absoluto" value={item.path_absoluto ?? '—'} mono />
                <Field label="Máquina" value={item.maquina ?? '—'} />
                <Field label="Origem" value={item.origem} />
                <Field label="Criado em" value={new Date(item.criado_em).toLocaleString('pt-BR')} />
                <Field label="Atualizado em" value={new Date(item.atualizado_em).toLocaleString('pt-BR')} />
              </div>
              {(item.tags.length > 0 || item.tech_stack.length > 0) && (
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    {item.tech_stack.length > 0 && (
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Tech stack</div>
                        <div className="flex flex-wrap gap-1.5">
                          {item.tech_stack.map((s) => (
                            <Badge key={s} variant="outline">{s}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {item.tags.length > 0 && (
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Tags</div>
                        <div className="flex flex-wrap gap-1.5">
                          {item.tags.map((t) => (
                            <Badge key={t} variant="secondary">#{t}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="connections">
          {!relationsData ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : relationsData.counts.outgoing + relationsData.counts.backlinks === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhuma conexão. Wikilinks em docs do Obsidian criam relações automáticas.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card>
                <CardContent className="pt-4">
                  <h3 className="font-medium text-sm mb-2">
                    Referenciado por ({relationsData.counts.backlinks})
                  </h3>
                  {relationsData.backlinks.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Ninguém referencia ainda.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {relationsData.backlinks.map((r) => (
                        <li key={r.id}>
                          <Link
                            to={`/items/${r.source_item_id}`}
                            className="flex items-center justify-between text-sm hover:text-primary"
                          >
                            <span className="truncate">{r.source_nome}</span>
                            <Badge variant="outline" className="text-[10px] ml-2">
                              {r.source_tipo}
                            </Badge>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <h3 className="font-medium text-sm mb-2">
                    Aponta para ({relationsData.counts.outgoing})
                  </h3>
                  {relationsData.outgoing.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhum link saindo.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {relationsData.outgoing.map((r) => (
                        <li key={r.id}>
                          <Link
                            to={`/items/${r.target_item_id}`}
                            className="flex items-center justify-between text-sm hover:text-primary"
                          >
                            <span className="truncate">{r.target_nome}</span>
                            <Badge variant="outline" className="text-[10px] ml-2">
                              {r.target_tipo}
                            </Badge>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history">
          {!versionsData || versionsData.versions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma versão anterior.</p>
          ) : (
            <div className="space-y-2">
              {versionsData.versions.map((v) => (
                <Card key={v.id}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground">
                        {new Date(v.criado_em).toLocaleString('pt-BR')} · {v.motivo}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {Object.keys(v.diff).length} campo{Object.keys(v.diff).length !== 1 ? 's' : ''} alterado{Object.keys(v.diff).length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="space-y-1 text-xs font-mono">
                      {Object.entries(v.diff).map(([field, change]) => (
                        <div key={field}>
                          <span className="font-semibold text-foreground">{field}:</span>{' '}
                          <span className="text-muted-foreground line-through">
                            {JSON.stringify(change.from)}
                          </span>{' '}
                          →{' '}
                          <span className="text-primary">{JSON.stringify(change.to)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-0.5">{label}</div>
      <div className={cn('text-sm break-all', mono && 'font-mono text-xs')}>{value}</div>
    </div>
  );
}
